import React, { useMemo } from 'react';
import { IntlShape, MessageDescriptor, useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import type { OutreachLiveSearch, OutreachSearchStep } from '../outreachTypes';
import { liveMessages as messages } from './liveMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the live screens; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Live.module.scss';
/* eslint-enable css-modules/no-unused-class */

const STEP_TEXT: Record<OutreachSearchStep['key'], { label: MessageDescriptor; sub: MessageDescriptor | null }> = {
  sound: { label: messages.stepSound, sub: null },
  similar: { label: messages.stepSimilar, sub: messages.stepSimilarSub },
  scan: { label: messages.stepScan, sub: messages.stepScanSub },
  contacts: { label: messages.stepContacts, sub: messages.stepContactsSub },
  write: { label: messages.stepWrite, sub: messages.stepWriteSub },
  send: { label: messages.stepSend, sub: messages.stepSendSub },
};

const STATE_TEXT: Record<OutreachSearchStep['state'], MessageDescriptor> = {
  done: messages.stepDone,
  now: messages.stepNow,
  wait: messages.stepWait,
};

/** The real counter of a step: "3 genres", "602 / 1,842", "24", or nothing before a number exists. */
function stepValue(intl: IntlShape, step: OutreachSearchStep): string {
  if (step.count === null) {
    return '';
  }
  if (step.key === 'sound') {
    return intl.formatMessage(messages.stepGenres, { count: step.count });
  }
  if (step.total !== null) {
    return intl.formatMessage(messages.stepOfTotal, {
      count: intl.formatNumber(step.count),
      total: intl.formatNumber(step.total),
    });
  }
  return intl.formatNumber(step.count);
}

/**
 * "What Plug does now" (design t1): the six steps with their real counters, and the sound that
 * Plug read (genres and "Like <artist>" chips).
 */
const SearchStepsComponent: React.FC<{ search: OutreachLiveSearch }> = ({ search }) => {
  const intl = useIntl();
  const genresLine = useMemo(() => search.genres.join(', '), [search.genres]);
  const rows = useMemo(
    () =>
      search.steps.map((step) => {
        const text = STEP_TEXT[step.key];
        const sub = step.key === 'sound' ? genresLine : text.sub === null ? '' : intl.formatMessage(text.sub);
        return (
          <li
            key={step.key}
            className={clsx(styles.step, step.state === 'now' && styles.stepNow)}
            data-testid={`search-step-${step.key}`}
            data-state={step.state}
          >
            {step.state === 'done' && (
              <span className={clsx(styles.stepIcon, styles.stepIconDone)} aria-hidden>
                <FontAwesomeIcon icon={faCheck} />
              </span>
            )}
            {step.state === 'now' && (
              <span className={styles.stepIcon} aria-hidden>
                <span className={styles.spin} />
              </span>
            )}
            {step.state === 'wait' && <span className={clsx(styles.stepIcon, styles.stepIconWait)} aria-hidden />}
            <span className="visually-hidden">{intl.formatMessage(STATE_TEXT[step.state])}</span>
            <span className={styles.stepText}>
              <b>{intl.formatMessage(text.label)}</b>
              {sub !== '' && <small>{sub}</small>}
            </span>
            <span className={styles.stepValue}>{stepValue(intl, step)}</span>
          </li>
        );
      }),
    [genresLine, intl, search.steps]
  );
  const chips = useMemo(
    () => [
      ...search.genres.map((genre) => (
        <span key={`g-${genre}`} className={shared.chip}>
          {genre}
        </span>
      )),
      ...search.similarArtists.map((name) => (
        <span key={`a-${name}`} className={shared.chip}>
          {intl.formatMessage(messages.likeArtist, { name })}
        </span>
      )),
    ],
    [intl, search.genres, search.similarArtists]
  );

  return (
    <section className={styles.card} aria-labelledby="outreach-search-steps">
      <h2 id="outreach-search-steps" className={styles.h2}>
        {intl.formatMessage(messages.stepsTitle)}
      </h2>
      <ol className={styles.steps}>{rows}</ol>
      {chips.length > 0 && (
        <div className={`${styles.inset} mt-auto`}>
          <span className={shared.eyebrow}>{intl.formatMessage(messages.yourSound)}</span>
          <div className={shared.row}>{chips}</div>
        </div>
      )}
    </section>
  );
};

export const SearchSteps = TypedMemo(SearchStepsComponent);
